import { z } from 'zod'
import { trunkDiameterSchema } from '../validators/decimal-string.validator.js'
import { moneyAmountSchema } from '../validators/money.validator.js'
import { uuidSchema } from '../validators/uuid.validator.js'
import { treeProblemDtoSchema } from './tree-problem.dto.js'
import { jobLevelSchema, jobTypeSchema } from '../enums/job-type.enum.js'
import { seasonSchema } from '../enums/season.enum.js'
import { placedPointSchema } from './geo.dto.js'
import { treeSpeciesMarkSchema } from './tree-species.dto.js'

/** One piece of work to be done on one tree. */
export const treeJobDtoSchema = z.object({
  type: jobTypeSchema,
  level: jobLevelSchema,
  /**
   * Crew minutes for this job, or null when the tree's time was never split.
   *
   * Itemizing is what makes a job declinable by the client — without a figure
   * there is nothing to take away, so the work stays in the price.
   */
  minutes: z.number().int().nonnegative().nullable(),
  /** What the OTHER work is. */
  notes: z.string().nullable(),
})

export type TreeJobDto = z.infer<typeof treeJobDtoSchema>

/**
 * A single surveyed tree.
 *
 * Coordinates cross the wire as numbers even though the column is a `Decimal`.
 * That differs from `SiteDto.acreage` on purpose: acreage is an exact decimal
 * quantity that must survive the round trip unchanged, while a coordinate is a
 * measurement handed straight to a mapping library, and every such library on
 * both sides takes doubles. A double carries far more significant digits than
 * the six decimal places (~0.11 m) the column stores, so nothing is lost —
 * and sending strings would mean parsing them back at every call site.
 */
export const treeDtoSchema = z.object({
  id: uuidSchema,
  siteId: uuidSchema,
  label: z.string(),
  /**
   * The species, or null when nobody has identified this tree yet.
   *
   * The id AND the mark travel together. A pin has to draw itself without a
   * second lookup — ninety of them render per screen — and the map, the legend
   * and a tree list all need the name. Carrying the mark also means a tree
   * whose species was archived still draws correctly.
   */
  speciesId: uuidSchema.nullable(),
  species: treeSpeciesMarkSchema.nullable(),

  ...placedPointSchema.shape,

  /**
   * Estimated crew time in whole minutes, or null when nobody has estimated it
   * yet — see `lib/src/helpers/minutes.ts`.
   *
   * A plain number, unlike the exact-decimal strings money and coordinates use.
   * Minutes are integers, so there is no precision to lose crossing the wire
   * and nothing to be gained by carrying them as text.
   */
  estimatedLaborMinutes: z.number().int().nonnegative().nullable(),
  /**
   * Everything found wrong with this tree, each with its own severity.
   *
   * A list rather than the flag, note and rating this replaced. Those three
   * columns could describe exactly one finding, so a tree with mistletoe AND a
   * split leader had to either lose one or blur both into a paragraph nothing
   * could sort, count or price.
   *
   * Empty is the normal case and means nothing is wrong — there is no separate
   * "has a problem" boolean to disagree with it. Anything that needs the flag
   * asks whether this list is empty, which cannot drift out of step with the
   * problems themselves.
   */
  problems: z.array(treeProblemDtoSchema),
  /**
   * Diameter at breast height in inches, as an exact decimal string. Null
   * when nobody has measured it — solutions priced by it simply do not price.
   * `Decimal(6, 2)`; the converter sends `toFixed(2)`.
   */
  dbhInches: trunkDiameterSchema.nullable(),
  /**
   * Whether removal is offered for this tree, and what it costs.
   *
   * Separate from the pruning jobs because it competes with them rather than
   * adding to them — a client choosing removal is choosing INSTEAD of
   * pruning, and instead of treating the tree's problems.
   */
  removalOffered: z.boolean(),
  removalMinutes: z.number().int().nonnegative().nullable(),
  removalNotes: z.string().nullable(),
  /**
   * The arborist recommends no pruning on this tree, and the proposal says
   * so. It carries no jobs, a zero estimate and no pruning price beside it.
   *
   * Defaulted, like every field added after phones began caching the survey.
   */
  noPruningRecommended: z.boolean().default(false),
  /**
   * Prices set by hand, in dollars, in place of the crew rate. Null is the
   * formula. Only the money changes; the minutes are what the crew is
   * scheduled by.
   */
  pruningPriceOverride: moneyAmountSchema.nullable().default(null),
  removalPriceOverride: moneyAmountSchema.nullable().default(null),
  /** Why a price on this tree was set by hand. Internal. */
  priceOverrideNotes: z.string().nullable().default(null),
  /**
   * Offered, but not part of what the arborist recommends.
   *
   * Defaulted because this schema also reads back the survey a phone cached
   * offline. A tree cached by a build before the flag existed has no such key,
   * and a required one would fail it: the tree would drop off the map until
   * the phone reached the server, and the cache would be quarantined.
   */
  isOptional: z.boolean().default(false),
  notes: z.string().nullable(),
  jobs: z.array(treeJobDtoSchema),
  /**
   * When this tree's pruning should be done: every job above, the estimate,
   * and removal when it is quoted, which goes with the pruning it competes
   * with (docs/seasonal-work.md). Null when no season was chosen, which a
   * proposal reads as "with the next visit".
   *
   * Defaulted for a tree a phone cached before seasons existed.
   */
  pruningSeason: seasonSchema.nullable().default(null),

  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type TreeDto = z.infer<typeof treeDtoSchema>

/**
 * Every tree on a site.
 *
 * Deliberately unpaginated. A survey is read as a whole — the map draws all of
 * it at once, and a page of trees is not a thing anyone wants. Sites top out in
 * the low hundreds of trees, so the full set is a few tens of kilobytes.
 */
export const treeListDtoSchema = z.object({
  siteId: uuidSchema,
  /**
   * The visit whose findings the trees show, or null when the site has none
   * for trees. Defaulted for an answer cached before visits existed.
   */
  visitId: uuidSchema.nullable().default(null),
  trees: z.array(treeDtoSchema),
})

export type TreeListDto = z.infer<typeof treeListDtoSchema>
